import React, { FC, Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useUrlQuery } from 'src/utils/useURLQuery';
import { tokens, Loading } from '@beeline.com/bp-design-system';
import { PAGE_ROUTES as Routes } from '../pageRoutes';
import { ExternalComplete } from './ExternalComplete';
import 'iframe-resizer/js/iframeResizer.contentWindow';
import { useExternalIntl } from './translations/useExternalIntl';

export const ExternalRoutes: FC<React.PropsWithChildren<{}>> = () => {
  const history = useHistory();
  const urlQuery = useUrlQuery();
  // A host embedding this page in a fixed-height iframe (e.g. the supplier app's milestone correction dialog) sets
  // ?layout=fixed so this page's own scroll is the only scroll container, instead of growing to fit all its content
  // like the default (auto-resizing iframe) mode.
  const isFixedLayout = urlQuery.get('layout') === 'fixed';
  const basePath = '/external';
  const smartformPath = `${basePath}/smartforms`;
  const completePath = `${basePath}/complete`;
  const addWorkerPath = `${smartformPath}/add-worker`;
  const correctMilestonePath = `${smartformPath}/correct-milestone`;
  const addJobApplicationPath = `${smartformPath}/job-application`;
  const offboardWorkerPath = `${smartformPath}/worker-offboarding`;
  const addEngagementPath = `${smartformPath}/add-engagement`;
  const updateEngagementPath = `${smartformPath}/update-engagement`;
  const taskPath = `${basePath}/tasks/:taskId`;
  const docYardPath = `${basePath}/docyard`;

  const intl = useExternalIntl();

  const routes = (
    <Suspense fallback={<Loading className="main_loader" />}>
      <Switch>
        <Route
          path={[
            addWorkerPath,
            addJobApplicationPath,
            offboardWorkerPath,
            addEngagementPath,
            updateEngagementPath,
            correctMilestonePath,
            intl.reviewTask,
            taskPath,
            docYardPath
          ]}
          render={() => (
            <Routes.Frontdoor
              external
              onComplete={() => {
                history.push(completePath);
                // Parent is used for an iframe
                window.parent?.postMessage(`formSubmitted_${urlQuery.get('code')}`, '*');
                // Opener is used for a tab
                window.opener?.postMessage(`formSubmitted_${urlQuery.get('code')}`, '*');
              }}
            />
          )}
        />
        <Route path={completePath} component={ExternalComplete} />
        <Route render={() => <span>{intl.routeNotFound}</span>} />
      </Switch>
    </Suspense>
  );

  const externalStyles: React.CSSProperties = {
    overflowX: 'hidden',
    overflowY: 'auto',
    backgroundColor: tokens.colorSurface,
    color: tokens.colorOnSurface,
    ...(isFixedLayout ? { height: '100vh' } : { minHeight: 550, height: '100%' })
  };

  return (
    <div className="app-wrapper" style={externalStyles}>
      {routes}
    </div>
  );
};
